import { useEffect, useState } from "react";
import IDXCarousel from "@/components/home/IDXCarousel";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Clock, TrendingDown, MapPin, Home, BookOpen } from "lucide-react";
import { Layout } from "@/components/layout";
import SearchBar from "@/components/SearchBar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { IDXDisclaimer } from "@/components/compliance";
import SocialShare from "@/components/social/SocialShare";
import { SEOHead } from "@/components/seo";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";

const HERO_IMAGE_WEBP = "/images/hero-home.webp";
const HERO_IMAGE_JPG = "/images/hero-home.jpg";

const COMMUNITY_LINKS = [
  { name: "Cherry Hill", slug: "cherry-hill-homes-for-sale" },
  { name: "Moorestown", slug: "moorestown-homes-for-sale" },
  { name: "Mount Laurel", slug: "mount-laurel-homes-for-sale" },
  { name: "Haddonfield", slug: "haddonfield-homes-for-sale" },
  { name: "Marlton", slug: "marlton-homes-for-sale" },
  { name: "Voorhees", slug: "voorhees-homes-for-sale" },
  { name: "Medford", slug: "medford-homes-for-sale" },
  { name: "Cinnaminson", slug: "cinnaminson-homes-for-sale" },
  { name: "Collingswood", slug: "collingswood-homes-for-sale" },
  { name: "Delran", slug: "delran-homes-for-sale" },
  { name: "Tabernacle", slug: "tabernacle-homes-for-sale" },
  { name: "Pennsauken", slug: "pennsauken-homes-for-sale" },
  { name: "Maple Shade", slug: "maple-shade-homes-for-sale" },
  { name: "Bellmawr", slug: "bellmawr-homes-for-sale" },
  { name: "Washington Township", slug: "washington-township-homes-for-sale" },
];

const Index = () => {
  const [isHeroLoaded, setIsHeroLoaded] = useState(true);
  const [latestPosts, setLatestPosts] = useState<any[]>([]);

  useEffect(() => {
    document.title = "South Jersey Homes for Sale | Prime Realty Partners";
  }, []);


  useEffect(() => {
    const fetchLatestPosts = async () => {
      const { data } = await supabase
        .from("blog_posts")
        .select("id, title, slug, excerpt, featured_image, category, published_at")
        .in("status", ["published", "scheduled"])
        .lte("published_at", new Date().toISOString())
        .order("published_at", { ascending: false })
        .limit(3);
      if (data) setLatestPosts(data);
    };
    fetchLatestPosts();
  }, []);

  const homepageSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": ["RealEstateAgent", "LocalBusiness"],
        "@id": "https://www.prpsj.com/#organization",
        "name": "Prime Realty Partners",
        "alternateName": "PRP",
        "description": "Independent South Jersey brokerage serving buyers, sellers, and investors from Philadelphia's suburbs to the Jersey Shore.",
        "url": "https://www.prpsj.com",
        "telephone": "+18562103000",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "31 Walnut Ave",
          "addressLocality": "Bellmawr",
          "addressRegion": "NJ",
          "postalCode": "08031",
          "addressCountry": "US"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": 39.865425,
          "longitude": -75.080862
        },
        "areaServed": [
          { "@type": "AdministrativeArea", "name": "Camden County, NJ" },
          { "@type": "AdministrativeArea", "name": "Burlington County, NJ" },
          { "@type": "AdministrativeArea", "name": "Gloucester County, NJ" },
          { "@type": "City", "name": "Audubon", "containedInPlace": { "@type": "AdministrativeArea", "name": "Camden County, NJ" } },
          { "@type": "City", "name": "Barrington", "containedInPlace": { "@type": "AdministrativeArea", "name": "Camden County, NJ" } },
          { "@type": "City", "name": "Bellmawr", "containedInPlace": { "@type": "AdministrativeArea", "name": "Camden County, NJ" } },
          { "@type": "City", "name": "Berlin", "containedInPlace": { "@type": "AdministrativeArea", "name": "Camden County, NJ" } },
          { "@type": "City", "name": "Cherry Hill", "containedInPlace": { "@type": "AdministrativeArea", "name": "Camden County, NJ" } },
          { "@type": "City", "name": "Clementon", "containedInPlace": { "@type": "AdministrativeArea", "name": "Camden County, NJ" } },
          { "@type": "City", "name": "Collingswood", "containedInPlace": { "@type": "AdministrativeArea", "name": "Camden County, NJ" } },
          { "@type": "City", "name": "Gloucester City", "containedInPlace": { "@type": "AdministrativeArea", "name": "Camden County, NJ" } },
          { "@type": "City", "name": "Gloucester Township", "containedInPlace": { "@type": "AdministrativeArea", "name": "Camden County, NJ" } },
          { "@type": "City", "name": "Haddon Heights", "containedInPlace": { "@type": "AdministrativeArea", "name": "Camden County, NJ" } },
          { "@type": "City", "name": "Haddon Township", "containedInPlace": { "@type": "AdministrativeArea", "name": "Camden County, NJ" } },
          { "@type": "City", "name": "Haddonfield", "containedInPlace": { "@type": "AdministrativeArea", "name": "Camden County, NJ" } },
          { "@type": "City", "name": "Lindenwold", "containedInPlace": { "@type": "AdministrativeArea", "name": "Camden County, NJ" } },
          { "@type": "City", "name": "Merchantville", "containedInPlace": { "@type": "AdministrativeArea", "name": "Camden County, NJ" } },
          { "@type": "City", "name": "Oaklyn", "containedInPlace": { "@type": "AdministrativeArea", "name": "Camden County, NJ" } },
          { "@type": "City", "name": "Pennsauken", "containedInPlace": { "@type": "AdministrativeArea", "name": "Camden County, NJ" } },
          { "@type": "City", "name": "Pine Hill", "containedInPlace": { "@type": "AdministrativeArea", "name": "Camden County, NJ" } },
          { "@type": "City", "name": "Runnemede", "containedInPlace": { "@type": "AdministrativeArea", "name": "Camden County, NJ" } },
          { "@type": "City", "name": "Somerdale", "containedInPlace": { "@type": "AdministrativeArea", "name": "Camden County, NJ" } },
          { "@type": "City", "name": "Stratford", "containedInPlace": { "@type": "AdministrativeArea", "name": "Camden County, NJ" } },
          { "@type": "City", "name": "Voorhees", "containedInPlace": { "@type": "AdministrativeArea", "name": "Camden County, NJ" } },
          { "@type": "City", "name": "Winslow Township", "containedInPlace": { "@type": "AdministrativeArea", "name": "Camden County, NJ" } },
          { "@type": "City", "name": "Bordentown", "containedInPlace": { "@type": "AdministrativeArea", "name": "Burlington County, NJ" } },
          { "@type": "City", "name": "Burlington City", "containedInPlace": { "@type": "AdministrativeArea", "name": "Burlington County, NJ" } },
          { "@type": "City", "name": "Burlington Township", "containedInPlace": { "@type": "AdministrativeArea", "name": "Burlington County, NJ" } },
          { "@type": "City", "name": "Cinnaminson", "containedInPlace": { "@type": "AdministrativeArea", "name": "Burlington County, NJ" } },
          { "@type": "City", "name": "Delran", "containedInPlace": { "@type": "AdministrativeArea", "name": "Burlington County, NJ" } },
          { "@type": "City", "name": "Florence", "containedInPlace": { "@type": "AdministrativeArea", "name": "Burlington County, NJ" } },
          { "@type": "City", "name": "Lumberton", "containedInPlace": { "@type": "AdministrativeArea", "name": "Burlington County, NJ" } },
          { "@type": "City", "name": "Maple Shade", "containedInPlace": { "@type": "AdministrativeArea", "name": "Burlington County, NJ" } },
          { "@type": "City", "name": "Marlton", "containedInPlace": { "@type": "AdministrativeArea", "name": "Burlington County, NJ" } },
          { "@type": "City", "name": "Medford", "containedInPlace": { "@type": "AdministrativeArea", "name": "Burlington County, NJ" } },
          { "@type": "City", "name": "Medford Lakes", "containedInPlace": { "@type": "AdministrativeArea", "name": "Burlington County, NJ" } },
          { "@type": "City", "name": "Moorestown", "containedInPlace": { "@type": "AdministrativeArea", "name": "Burlington County, NJ" } },
          { "@type": "City", "name": "Mount Holly", "containedInPlace": { "@type": "AdministrativeArea", "name": "Burlington County, NJ" } },
          { "@type": "City", "name": "Mount Laurel", "containedInPlace": { "@type": "AdministrativeArea", "name": "Burlington County, NJ" } },
          { "@type": "City", "name": "Palmyra", "containedInPlace": { "@type": "AdministrativeArea", "name": "Burlington County, NJ" } },
          { "@type": "City", "name": "Riverside", "containedInPlace": { "@type": "AdministrativeArea", "name": "Burlington County, NJ" } },
          { "@type": "City", "name": "Riverton", "containedInPlace": { "@type": "AdministrativeArea", "name": "Burlington County, NJ" } },
          { "@type": "City", "name": "Shamong", "containedInPlace": { "@type": "AdministrativeArea", "name": "Burlington County, NJ" } },
          { "@type": "City", "name": "Tabernacle", "containedInPlace": { "@type": "AdministrativeArea", "name": "Burlington County, NJ" } },
          { "@type": "City", "name": "Willingboro", "containedInPlace": { "@type": "AdministrativeArea", "name": "Burlington County, NJ" } },
          { "@type": "City", "name": "Clayton", "containedInPlace": { "@type": "AdministrativeArea", "name": "Gloucester County, NJ" } },
          { "@type": "City", "name": "Deptford", "containedInPlace": { "@type": "AdministrativeArea", "name": "Gloucester County, NJ" } },
          { "@type": "City", "name": "Franklin Township", "containedInPlace": { "@type": "AdministrativeArea", "name": "Gloucester County, NJ" } },
          { "@type": "City", "name": "Glassboro", "containedInPlace": { "@type": "AdministrativeArea", "name": "Gloucester County, NJ" } },
          { "@type": "City", "name": "Harrison Township", "containedInPlace": { "@type": "AdministrativeArea", "name": "Gloucester County, NJ" } },
          { "@type": "City", "name": "Logan Township", "containedInPlace": { "@type": "AdministrativeArea", "name": "Gloucester County, NJ" } },
          { "@type": "City", "name": "Mantua", "containedInPlace": { "@type": "AdministrativeArea", "name": "Gloucester County, NJ" } },
          { "@type": "City", "name": "Monroe Township", "containedInPlace": { "@type": "AdministrativeArea", "name": "Gloucester County, NJ" } },
          { "@type": "City", "name": "Mullica Hill", "containedInPlace": { "@type": "AdministrativeArea", "name": "Gloucester County, NJ" } },
          { "@type": "City", "name": "National Park", "containedInPlace": { "@type": "AdministrativeArea", "name": "Gloucester County, NJ" } },
          { "@type": "City", "name": "Paulsboro", "containedInPlace": { "@type": "AdministrativeArea", "name": "Gloucester County, NJ" } },
          { "@type": "City", "name": "Pitman", "containedInPlace": { "@type": "AdministrativeArea", "name": "Gloucester County, NJ" } },
          { "@type": "City", "name": "Swedesboro", "containedInPlace": { "@type": "AdministrativeArea", "name": "Gloucester County, NJ" } },
          { "@type": "City", "name": "Washington Township", "containedInPlace": { "@type": "AdministrativeArea", "name": "Gloucester County, NJ" } },
          { "@type": "City", "name": "Wenonah", "containedInPlace": { "@type": "AdministrativeArea", "name": "Gloucester County, NJ" } },
          { "@type": "City", "name": "West Deptford", "containedInPlace": { "@type": "AdministrativeArea", "name": "Gloucester County, NJ" } },
          { "@type": "City", "name": "Woodbury", "containedInPlace": { "@type": "AdministrativeArea", "name": "Gloucester County, NJ" } },
          { "@type": "City", "name": "Woolwich", "containedInPlace": { "@type": "AdministrativeArea", "name": "Gloucester County, NJ" } }
        ],
        "logo": {
          "@type": "ImageObject",
          "@id": "https://www.prpsj.com/#logo",
          "url": "https://www.prpsj.com/logo.png",
          "contentUrl": "https://www.prpsj.com/logo.png",
          "caption": "Prime Realty Partners"
        },
        "sameAs": [
          "https://www.facebook.com/MarcRicciPRP",
          "https://share.google/sD1fKreeezDW1acJg",
          "https://www.realtor.com/realestateagency/60d3874c1e0524001267678d",
          "https://www.realtor.com/realestateagency/634405031e077903e9540014",
          "https://shophaddon.com/business/prime-realty-partners/",
          "https://www.nestfully.com/Office/PrimeRealtyPartners-143302",
          "https://www.realty.com/office/483826/prime_realty_partners",
          "https://www.fastexpert.com/real-estate-company/prime-realty-partners-haddon-township-nj-49952/",
          "https://www.landsearch.com/agents/prime-realty-partners/670122"
        ]
      },
      {
        "@type": "WebSite",
        "@id": "https://www.prpsj.com/#website",
        "name": "Prime Realty Partners",
        "url": "https://www.prpsj.com",
        "publisher": {
          "@id": "https://www.prpsj.com/#organization"
        },
        "inLanguage": "en-US"
      }
    ]
  };

  return (
    <Layout>
      <SEOHead
        title="South Jersey Homes for Sale | Prime Realty Partners"
        description="Search homes for sale across 49 South Jersey communities. Prime Realty Partners — your local experts in Camden, Burlington & Gloucester counties."
        canonicalUrl="https://www.prpsj.com"
        ogType="website"
        keywords="South Jersey homes for sale, South Jersey real estate, Camden County real estate, Burlington County homes for sale, Gloucester County real estate, NJ homes for sale"
        schema={homepageSchema}
      />

      {/* Hero Section */}
      <section className="relative min-h-[600px] md:min-h-[700px] flex items-center justify-center">
        {/* Placeholder gradient (visible until image loads) */}
        <div 
          className={`absolute inset-0 bg-gradient-to-br from-slate-800 via-slate-700 to-slate-900 transition-opacity duration-500 ${isHeroLoaded ? 'opacity-0' : 'opacity-100'}`}
          aria-hidden="true"
        />
        
        {/* Optimized Hero Image with WebP support */}
        <picture>
          <source srcSet={HERO_IMAGE_WEBP} type="image/webp" />
          <img
            src={HERO_IMAGE_JPG}
            alt="Homes for sale in South Jersey — Prime Realty Partners"
            width={1344}
            height={768}
            loading="eager"
            decoding="sync"
            fetchPriority="high"
            onLoad={() => setIsHeroLoaded(true)}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${isHeroLoaded ? 'opacity-100' : 'opacity-0'}`}
          />
        </picture>
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" aria-hidden="true" />

        {/* Hero Content */}
        <div className="relative z-10 container px-4 py-16 md:py-24">
          <div className="max-w-3xl mx-auto text-center mb-8">
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
              Find Your Dream Home
            </h1>
            <p className="text-lg md:text-xl text-white/90 mb-8 animate-fade-up" style={{ animationDelay: "0.1s" }}>
              Browse listings, explore local markets, and connect with expert agents at Prime Realty Partners—your trusted partner in real estate.
            </p>
          </div>

          {/* Search Bar */}
          <div className="max-w-4xl mx-auto animate-fade-up" style={{ animationDelay: "0.2s" }}>
            <SearchBar variant="hero" />
          </div>
        </div>
      </section>

      {/* Social Share Section */}
      <section className="py-4 bg-muted/30">
        <div className="container flex justify-center">
          <SocialShare 
            title="Prime Realty Partners - Find Your Dream Home" 
            showLabel 
          />
        </div>
      </section>

      {/* Featured Listings Carousel */}
      <section className="py-10 md:py-14 bg-background">
        <div className="container">
          <div className="flex items-center justify-between mb-8">
            <div>
              <div className="flex items-center gap-2 text-primary mb-2">
                <Sparkles className="h-5 w-5" />
                <span className="text-sm font-medium uppercase tracking-wide">Featured</span>
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold">
                Featured Properties
              </h2>
            </div>
            <a href="https://homes.prpsj.com/idx/search/basic" rel="noopener noreferrer">
              <Button variant="ghost" className="gap-2">
                View All <ArrowRight className="h-4 w-4" />
              </Button>
            </a>
          </div>

          <IDXCarousel useFeatured={true} viewAllUrl="https://homes.prpsj.com/idx/search/basic" />
        </div>
      </section>

      {/* New This Week Section */}
      <section className="py-10 md:py-14 bg-muted/30">
        <div className="container">
          <div className="flex items-center justify-between mb-8">
            <div>
              <div className="flex items-center gap-2 text-primary mb-2">
                <Clock className="h-5 w-5" />
                <span className="text-sm font-medium uppercase tracking-wide">New</span>
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold">
                New This Week
              </h2>
            </div>
            <a href="https://homes.prpsj.com/i/new-this-week" rel="noopener noreferrer">
              <Button variant="ghost" className="gap-2">
                View All <ArrowRight className="h-4 w-4" />
              </Button>
            </a>
          </div>

          <IDXCarousel savedLinkId="10345" viewAllUrl="https://homes.prpsj.com/i/new-this-week" />
        </div>
      </section>

      {/* Price Reduced Section */}
      <section className="py-10 md:py-14 bg-background">
        <div className="container">
          <div className="flex items-center justify-between mb-8">
            <div>
              <div className="flex items-center gap-2 text-primary mb-2">
                <TrendingDown className="h-5 w-5" />
                <span className="text-sm font-medium uppercase tracking-wide">Reduced</span>
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold">
                Price Reduced
              </h2>
            </div>
            <a href="https://homes.prpsj.com/i/price-reduced" rel="noopener noreferrer">
              <Button variant="ghost" className="gap-2">
                View All <ArrowRight className="h-4 w-4" />
              </Button>
            </a>
          </div>

          <IDXCarousel savedLinkId="10346" viewAllUrl="https://homes.prpsj.com/i/price-reduced" />
        </div>
      </section>

      {/* Communities We Serve */}
      <section className="py-14 md:py-20 bg-muted/30">
        <div className="container">
          <div className="text-center mb-10">
            <div className="flex items-center justify-center gap-2 text-primary mb-2">
              <MapPin className="h-5 w-5" />
              <span className="text-sm font-medium uppercase tracking-wide">Local Experts</span>
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-3">
              Communities We Serve
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Explore homes for sale across South Jersey's top communities in Camden, Burlington, and Gloucester counties.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {COMMUNITY_LINKS.map((community) => (
              <Link
                key={community.slug}
                to={`/${community.slug}`}
                className="group flex items-center gap-2 rounded-lg border border-border bg-background px-4 py-3 transition-all hover:border-primary hover:shadow-md"
              >
                <MapPin className="h-4 w-4 text-primary shrink-0" />
                <span className="text-sm font-medium group-hover:text-primary transition-colors">
                  {community.name}
                </span>
              </Link>
            ))}
          </div>

          <div className="flex flex-wrap items-center justify-center gap-4 mt-8">
            <Link to="/communities">
              <Button variant="default" className="gap-2">
                View All Communities <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
            <Link to="/sold">
              <Button variant="outline" className="gap-2">
                <Home className="h-4 w-4" />
                Recently Sold
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Data Source Notice */}
      <section className="py-8 bg-background">
        <div className="container">
          <div className="text-center text-xs text-muted-foreground space-y-2">
            <p>Data updated frequently from IDX Broker.</p>
            <IDXDisclaimer variant="short" />
          </div>
        </div>
      </section>

      {/* Latest from the Blog */}
      {latestPosts.length > 0 && (
        <section className="py-14 md:py-20 bg-muted/30">
          <div className="container">
            <div className="flex items-start md:items-center justify-between mb-8 flex-col md:flex-row gap-4">
              <div>
                <div className="flex items-center gap-2 text-primary mb-2">
                  <BookOpen className="h-5 w-5" />
                  <span className="text-sm font-medium uppercase tracking-wide">From the Blog</span>
                </div>
                <h2 className="font-heading text-3xl md:text-4xl font-bold">
                  South Jersey Real Estate Insights
                </h2>
              </div>
              <Link to="/blog" className="text-primary hover:underline flex items-center gap-1 font-medium">
                View All Posts <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {latestPosts.map((post) => (
                <Link key={post.id} to={`/blog/${post.slug}`}>
                  <Card className="overflow-hidden hover:shadow-lg transition-shadow h-full">
                    {post.featured_image ? (
                      <img
                        src={post.featured_image}
                        alt={post.title}
                        width={600}
                        height={300}
                        className="w-full h-48 object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-48 bg-muted flex items-center justify-center">
                        <BookOpen className="h-12 w-12 text-muted-foreground/40" />
                      </div>
                    )}
                    <CardContent className="p-5 flex flex-col gap-3">
                      {post.category && (
                        <Badge variant="default" className="w-fit text-xs">
                          {post.category}
                        </Badge>
                      )}
                      <h3 className="font-heading text-lg font-semibold line-clamp-2">
                        {post.title}
                      </h3>
                      {post.excerpt && (
                        <p className="text-sm text-muted-foreground line-clamp-3">
                          {post.excerpt}
                        </p>
                      )}
                      <div className="flex items-center justify-between text-xs text-muted-foreground mt-auto pt-2">
                        <span>
                          {post.published_at
                            ? format(new Date(post.published_at), "MMM d, yyyy")
                            : ""}
                        </span>
                        <span className="text-primary font-medium">Read more →</span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-primary text-primary-foreground">
        <div className="container text-center">
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
            Ready to Find Your Perfect Home?
          </h2>
          <p className="text-lg md:text-xl text-primary-foreground/90 mb-8 max-w-2xl mx-auto">
            Our expert agents are here to guide you every step of the way. 
            Let's start your home search today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="https://homes.prpsj.com/idx/results/listings" rel="noopener noreferrer">
              <Button size="lg" variant="secondary" className="gap-2">
                Browse Properties <ArrowRight className="h-5 w-5" />
              </Button>
            </a>
            <Link to="/contact">
              <Button size="lg" className="bg-primary-foreground text-primary hover:bg-primary-foreground/90">
                Contact an Agent
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Index;
